import React from 'react';
import { Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLaptopCode,
  faCode,
  faBriefcase,
  faGraduationCap,
  faCertificate,
  faAward,
  faLanguage,
  faHandsHelping,
  faFileAlt,
  faCodeBranch,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

// Import the new proficiency renderer
// import { renderProficiencyVisualization } from '../styles/proficiency/ProficiencyRenderer';

// --- Constants ---
const DEFAULT_PRIMARY_COLOR = '#000';
const DEFAULT_SECONDARY_COLOR = '#333';
const DEFAULT_TERTIARY_COLOR = '#333';
const DEFAULT_QUATERNARY_COLOR = '#333';
const DEFAULT_TEXT_COLOR = '#000';
const DEFAULT_TEXT_SECONDARY_COLOR = '#666666';
const TIMELINE_LINE_WIDTH = '2px';
const TIMELINE_CIRCLE_RADIUS = 4;
const TIMELINE_CIRCLE_CY_SIDEBAR = 5;
const TIMELINE_CIRCLE_CY_OTHER = 10; // Classic/Topbar
const TIMELINE_CIRCLE_LEFT_SIDEBAR = '5.5'; // cx for sidebar circle svg
const TIMELINE_CIRCLE_LEFT_OTHER = '5.5'; // cx for classic/topbar circle svg
const TIMELINE_LINE_LEFT_SIDEBAR = '-.5px'; // Position line based on circle cx - Reverted to original WorkExp value
const TIMELINE_LINE_LEFT_OTHER = '102.4px'; // Position line for classic/topbar date alignment - Kept original WorkExp value
const TIMELINE_SECTION_PADDING_SIDEBAR = '15px'; // Padding when timeline active in sidebar
const TIMELINE_SECTION_PADDING_OTHER = 0; // No extra padding for classic/topbar
const DATE_BOX_WIDTH_OTHER = '90px'; // Width for date box in classic/topbar

// --- Helper Functions ---

// Format date helper
const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("default", {
    month: "short",
    year: "numeric",
  });
};

// Safe render helper
const safeRender = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch (e) { return '[Object]'; }
  }
  return String(value);
};

// Render sub-items helper
const renderSubItems = (items, renderFunction) => {
  if (!items || !Array.isArray(items)) return null;
  return items.map((item, index) => renderFunction(item, index));
};

// Render Section Header
const renderSectionHeader = (title, icon, sectionColor, classes, activeStyle) => {
  const showBorder = activeStyle?.settings?.showSectionTitleBorder;
  const useIcons = activeStyle?.settings?.useSectionIcons;
  const sectionBorderColor = showBorder ? sectionColor : 'transparent';

  return (
    <Box className={classes.sectionHeader} style={{ borderBottomColor: sectionBorderColor }}>
      {useIcons && icon && (
        <FontAwesomeIcon icon={icon} className={classes.sectionIcon} style={{ color: sectionColor }}/>
      )}
      <Typography className="sectionTitle" style={{ color: sectionColor }} sx={{ marginLeft: useIcons && icon ? undefined : '0px' }}>
        {title}
      </Typography>
    </Box>
  );
};

// Get Timeline Parameters
const getTimelineParams = (layoutType, isTimelineActive, colors) => {
  const timelineColor = colors?.primary || DEFAULT_PRIMARY_COLOR;
  const circleCy = layoutType === 'sidebar' ? TIMELINE_CIRCLE_CY_SIDEBAR : TIMELINE_CIRCLE_CY_OTHER;

  if (!isTimelineActive) {
    return {
      isTimelineActive: false,
      lineLeftPosition: '0px',
      sectionContentPaddingLeft: 0,
      timelineColor,
      circleCy,
      circleCx: TIMELINE_CIRCLE_LEFT_OTHER,
    };
  }

  if (layoutType === 'sidebar') {
    return {
      isTimelineActive: true,
      lineLeftPosition: TIMELINE_LINE_LEFT_SIDEBAR,
      sectionContentPaddingLeft: TIMELINE_SECTION_PADDING_SIDEBAR,
      timelineColor,
      circleCy,
      circleCx: TIMELINE_CIRCLE_LEFT_SIDEBAR,
    };
  } else { // Classic / Topbar
    return {
      isTimelineActive: true,
      lineLeftPosition: TIMELINE_LINE_LEFT_OTHER,
      sectionContentPaddingLeft: TIMELINE_SECTION_PADDING_OTHER,
      timelineColor,
      circleCy,
      circleCx: TIMELINE_CIRCLE_LEFT_OTHER,
    };
  }
};

// Render Timeline SVG Circle Element
const renderTimelineSvgCircle = (timelineParams) => (
  <svg width={timelineParams.circleCy * 2} height={timelineParams.circleCy * 2} style={{ display: 'block', overflow: 'visible' }}>
      <circle cx={timelineParams.circleCx} cy={timelineParams.circleCy} r={TIMELINE_CIRCLE_RADIUS} fill={timelineParams.timelineColor} />
  </svg>
);

// Render Timeline Element (Circle Container)
const renderTimelineElement = (layoutType, timelineParams) => {
  const svgCircle = renderTimelineSvgCircle(timelineParams);

  if (layoutType === 'sidebar') {
      return (
          <Box sx={{
              position: 'absolute',
              marginLeft: '-20px',
              marginTop: '3px',
              width: '15px',
              height: `${timelineParams.circleCy}px`,
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start'
          }}>
              {svgCircle}
          </Box>
      );
  } else { // Classic / Topbar
      return (
          <Box sx={{
              width: '10px', // Consistent width
              marginTop: '-3px',
              minHeight: `${timelineParams.circleCy * 1}px`,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              pt: '5px'
          }}>
              {svgCircle}
          </Box>
      );
  }
};

// Render Timeline Vertical Line
const renderTimelineLine = (timelineParams) => (
  <Box sx={{
      position: 'absolute',
      marginTop: '4px',
      left: timelineParams.lineLeftPosition,
      top: `${timelineParams.circleCy / 2}px`, // Start slightly below top circle center
      bottom: `${timelineParams.circleCy / 2}px`, // End slightly above bottom circle center
      width: TIMELINE_LINE_WIDTH,
      bgcolor: timelineParams.timelineColor,
      zIndex: 0,
  }}/>
);

// Format common item data for sections with potential timelines
const formatTimelineItemData = (item, dateFields = { start: 'startDate', end: 'endDate', current: 'current' }) => {
    const startDateFormatted = formatDate(item[dateFields.start]);
    const endDateFormatted = item[dateFields.current] ? 'Present' : formatDate(item[dateFields.end]);
    const hasDate = !!(item[dateFields.start] || item[dateFields.end] || item[dateFields.current]);
    let dateString = '';
    if (hasDate) {
      dateString = `${startDateFormatted}${endDateFormatted ? ` - ${endDateFormatted}` : ''}`;
    }

    // Default structure - specific sections might override parts of this
    const titleElements = [
      item.title ? <Typography component="span" className="itemTitle">{item.title}</Typography> : null, // Generic 'title'
      item.subtitle ? <Typography component="span" className="itemSubtitle">{item.subtitle}</Typography> : null, // Generic 'subtitle'
      item.location ? <Typography component="span" className="itemSubtitle">{item.location}</Typography> : null, // Common field
    ].filter(Boolean);

    return {
        hasDate,
        startDateFormatted,
        endDateFormatted,
        dateString,
        titleElements,
        description: item.description || '',
        link: item.link || item.url, // Common link/url fields
    };
};

// --- Base Layout Class ---
export class BaseLayout {
  // Assign static helpers
  static formatDate = formatDate;
  static renderSubItems = renderSubItems;
  static safeRender = safeRender;

  // Section rendering functions
  static renderProfessionalSummary(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    if (!data?.professionalSummary) return null;
    const sectionColor = colors?.primary || DEFAULT_PRIMARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Professional Summary') : 'Professional Summary';

    return (
      <Box className={classes.section} data-section="Professional Summary">
        {renderSectionHeader(sectionTitle, faFileAlt, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent}>
          <div className="richText" dangerouslySetInnerHTML={{ __html: this.safeRender(data.professionalSummary) }}/>
        </Box>
      </Box>
    );
  }

  // --- WORK EXPERIENCES (Refactored - Template for Timeline Sections) ---
  static renderWorkExperiences(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.workExperiences;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_SECONDARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Work Experience') : "Work Experience";
    const sectionIcon = faBriefcase;
    const totalItems = sectionData.length;

    // Determine if timeline is active for this section
    const sectionHasDates = sectionData.some(item => item.startDate || item.endDate || item.current);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    // Get timeline parameters
    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Work Experience">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {/* Render timeline line if active */}
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}

          {/* Render items */}
          {this.renderSubItems(sectionData, (item, index) => {
            // Format specific data for Work Experience
            const { hasDate, startDateFormatted, endDateFormatted, dateString, description, link } = formatTimelineItemData(item);
            const titleElements = [
              item.companyName ? <Typography component="span" className="itemTitle">{item.companyName}</Typography> : null,
              item.jobTitle ? <Typography component="span" className="itemSubtitle">{item.jobTitle}</Typography> : null,
              item.location ? <Typography component="span" className="itemSubtitle">{item.location}</Typography> : null,
            ].filter(Boolean);

            // Render timeline element only if the item has a date and timeline is active
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

            // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
              return (
                <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                  {timelineCircleElement}
                  {/* Adjust margin if timeline is active but item has no date */}
                  <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                      {hasDate && (
                          <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateString}</Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                          {titleElements.map((el, idx) => (
                             <React.Fragment key={idx}>
                                {el}
                                {idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}
                             </React.Fragment>
                          ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && (
                          <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>
                      )}
                  </Box>
                </Box>
              );
            }
            // Classic / Topbar Layout Structure
            else {
              return (
                <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                  {/* Date Box - Render conditionally */}
                  {(hasDate || timelineParams.isTimelineActive) ? (
                    <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center', pt: '5px' }}>
                       {hasDate && (<>
                           <Typography className="itemDate" component="div">{startDateFormatted}</Typography>
                           {endDateFormatted && (<>
                              <Typography className="itemDate" component="div" sx={{ lineHeight: 1 }}>-</Typography>
                              <Typography className="itemDate" component="div">{endDateFormatted}</Typography>
                           </>)}
                       </>)}
                       {/* Placeholder for alignment when timeline active but no date */}
                       {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                    </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/> /* Empty box if no date and no timeline */
                  }

                  {/* Timeline Circle */}
                  {timelineCircleElement}
                  {/* Spacer for alignment when timeline active but no date */}
                  {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}

                  {/* Main Content */}
                  <Box sx={{ flexGrow: 1, mt: hasDate ? 0 : '12px' /* Adjust top margin if no date */ }}>
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', mb: 0.5, alignItems: 'baseline' }}>
                        {titleElements.map((el, idx) => (
                           <React.Fragment key={idx}>
                              {el}
                              {idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}
                           </React.Fragment>
                        ))}
                     </Box>
                     <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                     {link && (
                       <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>
                     )}
                  </Box>
               </Box>
              );
            }
          })}
        </Box>
      </Box>
    );
  }

  // --- EDUCATIONS (Refactored - Uses Timeline Logic) ---
  static renderEducations(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.educations;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_SECONDARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Education') : "Education";
    const sectionIcon = faGraduationCap;
    const totalItems = sectionData.length;

    // Determine if timeline is active for this section
    const sectionHasDates = sectionData.some(item => item.startDate || item.endDate);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    // Get timeline parameters
    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Education">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}
          {this.renderSubItems(sectionData, (item, index) => {
            const { hasDate, startDateFormatted, endDateFormatted, dateString, description, link } = formatTimelineItemData(item);
            const titleElements = [
              item.schoolName ? <Typography component="span" className="itemTitle">{item.schoolName}</Typography> : null,
              item.degree ? <Typography component="span" className="itemSubtitle">{item.degree}</Typography> : null,
              item.fieldOfStudy ? <Typography component="span" className="itemSubtitle">{item.fieldOfStudy}</Typography> : null,
            ].filter(Boolean);
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

            // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
              return (
                <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                  {timelineCircleElement}
                  <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                      {hasDate && <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateString}</Typography>}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                          {titleElements.map((el, idx) => (
                             <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                          ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                  </Box>
                </Box>
              );
            }
            // Classic / Topbar Layout Structure
            else {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                   {(hasDate || timelineParams.isTimelineActive) ? (
                     <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center', pt: '10px' }}>
                        {hasDate && (<>
                            <Typography className="itemDate" component="div">{startDateFormatted}</Typography>
                            {endDateFormatted && (<>
                               <Typography className="itemDate" component="div" sx={{ lineHeight: 1 }}>-</Typography>
                               <Typography className="itemDate" component="div">{endDateFormatted}</Typography>
                            </>)}
                        </>)}
                        {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                     </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/>
                   }
                   {timelineCircleElement}
                   {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}
                   <Box sx={{ flexGrow: 1, mt: hasDate ? 0 : '12px' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', mb: 0.5, alignItems: 'baseline' }}>
                         {titleElements.map((el, idx) => (
                           <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                         ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
          })}
        </Box>
      </Box>
    );
  }

  static renderSkills(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, activeBarStyle, t) {
    if (!data?.skills || data.skills.length === 0) return null;
    const sectionColor = colors?.secondary || DEFAULT_TERTIARY_COLOR;
    const ActiveProficiencyComponent = activeBarStyle?.component;
    const sectionTitle = t ? t('inputSection.sections.Skills') : 'Skills';

    return (
      <Box className={classes.section} data-section="Skills">
        {renderSectionHeader(sectionTitle, faCode, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent}>
          {this.renderSubItems(data.skills, (item, index) => (
            <Box key={item.id || index} className={classes.sectionItem}>
              {item.displayFormat === 'individual' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography className="itemTitle" sx={{ marginRight: 'auto' }}>
                    {item.skillName}
                  </Typography>
                  {ActiveProficiencyComponent && item.proficiency && 
                    <ActiveProficiencyComponent proficiency={item.proficiency} classes={classes} colors={colors} />
                  }
                </Box>
              ) : (
                <>
                  <Typography className="itemTitle">
                    {item.category}
                  </Typography>
                  <Typography className="itemDescription">
                    {item.skills}
                  </Typography>
                </>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // --- PROJECTS (Refactored - Uses Timeline Logic) ---
  static renderProjects(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.projects;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_QUATERNARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Projects') : "Projects";
    const sectionIcon = faCodeBranch;
    const totalItems = sectionData.length;

    const sectionHasDates = sectionData.some(item => item.startDate || item.endDate || item.current);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Projects">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}
          {this.renderSubItems(sectionData, (item, index) => {
            const { hasDate, startDateFormatted, endDateFormatted, dateString, description, link } = formatTimelineItemData(item);
            const titleElements = [
              item.name ? <Typography component="span" className="itemTitle">{item.name}</Typography> : null,
              item.technologies ? <Typography component="span" className="itemSubtitle">{item.technologies}</Typography> : null,
            ].filter(Boolean);
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

            // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                   {timelineCircleElement}
                   <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                       {hasDate && <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateString}</Typography>}
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                         {titleElements.map((el, idx) => (
                           <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                         ))}
                       </Box>
                       <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                       {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
            // Classic / Topbar Layout Structure
            else {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                   {(hasDate || timelineParams.isTimelineActive) ? (
                     <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center', pt: '10px' }}>
                        {hasDate && (<>
                           <Typography className="itemDate" component="div">{startDateFormatted}</Typography>
                           {endDateFormatted && (<>
                              <Typography className="itemDate" component="div" sx={{ lineHeight: 1 }}>-</Typography>
                              <Typography className="itemDate" component="div">{endDateFormatted}</Typography>
                           </>)}
                        </>)}
                        {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                     </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/>
                   }
                   {timelineCircleElement}
                   {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}
                   <Box sx={{ flexGrow: 1, mt: hasDate ? 0 : '12px' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: ' 0.5em', mb: 0.5, alignItems: 'baseline' }}>
                        {titleElements.map((el, idx) => (
                          <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                        ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
          })}
        </Box>
      </Box>
    );
  }

  static renderCertifications(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    if (!data?.certifications || data.certifications.length === 0) return null;
    const sectionColor = colors?.secondary || DEFAULT_TERTIARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Certifications') : 'Certifications';

    return (
      <Box className={classes.section} data-section="Certifications">
        {renderSectionHeader(sectionTitle, faCertificate, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent}>
          {this.renderSubItems(data.certifications, (item, index) => {
            // Certification specific date formatting
            const issueDateFormatted = this.formatDate(item.issueDate || item.date);
            const expirationDateFormatted = (!item.doesNotExpire && item.expirationDate) ? this.formatDate(item.expirationDate) : null;
            const dateString = `${issueDateFormatted}${expirationDateFormatted ? ` - ${expirationDateFormatted}` : (item.doesNotExpire ? ' - No Expiration' : '')}`;

            return (
            <Box key={item.id || index} className={classes.sectionItem}>
              {item.credentialUrl ? (
                <a href={item.credentialUrl} className="itemLink" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <Typography className="itemTitle" sx={{ display: 'inline' }}>
                      {item.name || item.certificationName}
                  </Typography>
                </a>
              ) : (
                <Typography className="itemTitle">
                    {item.name || item.certificationName}
                </Typography>
              )}
              <Typography className="itemSubtitle">
                  {item.issuer || item.issuingOrganization}
                {item.credentialId && ` | ID: ${item.credentialId}`}
              </Typography>
              <Typography className="itemDate">{dateString}</Typography>
              {item.description && (
                <div className="richText" dangerouslySetInnerHTML={{ __html: this.safeRender(item.description) }} />
              )}
            </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // --- AWARDS & HONORS (Refactored - Uses Timeline Logic) ---
  static renderAwardsAndHonors(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.awardsAndHonors;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_QUATERNARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Awards and Honors') : "Awards and Honors";
    const sectionIcon = faAward;
    const totalItems = sectionData.length;

    // Only one date field 'date' for awards
    const sectionHasDates = sectionData.some(item => item.date);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Awards and Honors">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}
          {this.renderSubItems(sectionData, (item, index) => {
            // Use specific date field 'date'
            const { hasDate, description, link } = formatTimelineItemData(item, { start: 'date', end: null, current: null });
            const dateFormatted = hasDate ? this.formatDate(item.date) : ''; // Format the single date
            const titleElements = [
              item.title ? <Typography component="span" className="itemTitle">{item.title}</Typography> : null,
              item.issuer ? <Typography component="span" className="itemSubtitle">{item.issuer}</Typography> : null,
            ].filter(Boolean);
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

            // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
              return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                   {timelineCircleElement}
                   <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                       {hasDate && <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateFormatted}</Typography>}
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                         {titleElements.map((el, idx) => (
                           <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                         ))}
                       </Box>
                       <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                       {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
            // Classic / Topbar Layout Structure
            else {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                   {(hasDate || timelineParams.isTimelineActive) ? (
                     <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center' }}>
                        {hasDate && <Typography className="itemDate" component="div">{dateFormatted}</Typography>}
                        {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                     </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/>
                   }
                   {timelineCircleElement}
                   {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}
                   <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', mb: 0.5, alignItems: 'baseline' }}>
                        {titleElements.map((el, idx) => (
                          <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                        ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
          })}
        </Box>
      </Box>
    );
  }

  static renderLanguages(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, activeBarStyle, t) {
    if (!data?.languages || data.languages.length === 0) return null;
    const sectionColor = colors?.secondary || DEFAULT_TERTIARY_COLOR;
    const ActiveProficiencyComponent = activeBarStyle?.component;
    const sectionTitle = t ? t('inputSection.sections.Languages') : 'Languages';

    return (
      <Box className={classes.section} data-section="Languages">
        {renderSectionHeader(sectionTitle, faLanguage, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent}>
          {this.renderSubItems(data.languages, (item, index) => (
            <Box key={item.id || index} className={classes.sectionItem}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography className="itemTitle" sx={{ marginRight: 'auto' }}>
                  {item.language}
                </Typography>
                {ActiveProficiencyComponent && item.proficiency && 
                  <ActiveProficiencyComponent proficiency={item.proficiency} classes={classes} colors={colors} />
                }
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // --- VOLUNTEER EXPERIENCE (Refactored - Uses Timeline Logic) ---
  static renderVolunteerExperiences(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.volunteerExperiences;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_QUATERNARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Volunteer Experience') : "Volunteer Experience";
    const sectionIcon = faHandsHelping;
    const totalItems = sectionData.length;

    const sectionHasDates = sectionData.some(item => item.startDate || item.endDate || item.current);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Volunteer Experience">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}
          {this.renderSubItems(sectionData, (item, index) => {
            const { hasDate, startDateFormatted, endDateFormatted, dateString, description, link } = formatTimelineItemData(item);
            const titleElements = [
              item.organization ? <Typography component="span" className="itemTitle">{item.organization}</Typography> : null,
              item.role ? <Typography component="span" className="itemSubtitle">{item.role}</Typography> : null,
            ].filter(Boolean);
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

             // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                   {timelineCircleElement}
                   <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                       {hasDate && <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateString}</Typography>}
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                          {titleElements.map((el, idx) => (
                             <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                          ))}
                       </Box>
                       <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                       {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
            // Classic / Topbar Layout Structure
            else {
              return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                   {(hasDate || timelineParams.isTimelineActive) ? (
                     <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center' }}>
                        {hasDate && (<>
                           <Typography className="itemDate" component="div">{startDateFormatted}</Typography>
                           {endDateFormatted && (<>
                              <Typography className="itemDate" component="div" sx={{ lineHeight: 1 }}>-</Typography>
                              <Typography className="itemDate" component="div">{endDateFormatted}</Typography>
                           </>)}
                        </>)}
                        {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                     </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/>
                   }
                   {timelineCircleElement}
                   {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}
                   <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', mb: 0.5, alignItems: 'baseline' }}>
                        {titleElements.map((el, idx) => (
                          <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                        ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
          })}
        </Box>
      </Box>
    );
  }

  // --- PUBLICATIONS (Refactored - Uses Timeline Logic) ---
  static renderPublications(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    const sectionData = data?.publications;
    if (!sectionData || sectionData.length === 0) return null;

    const sectionColor = colors?.secondary || DEFAULT_QUATERNARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.Publications') : "Publications";
    const sectionIcon = faFileAlt;
    const totalItems = sectionData.length;

    const sectionHasDates = sectionData.some(item => item.date);
    const isTimelineGloballyActive = selectedSpecialEffectId === 'timeline';
    const isTimelineActiveForSection = isTimelineGloballyActive && sectionHasDates;

    const timelineParams = getTimelineParams(layoutType, isTimelineActiveForSection, colors);

    return (
      <Box className={classes.section} data-section="Publications">
        {renderSectionHeader(sectionTitle, sectionIcon, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent} sx={{ position: 'relative', pl: timelineParams.sectionContentPaddingLeft }}>
          {timelineParams.isTimelineActive && totalItems > 0 && renderTimelineLine(timelineParams)}
          {this.renderSubItems(sectionData, (item, index) => {
            const { hasDate, description, link } = formatTimelineItemData(item, { start: 'date', end: null, current: null });
            const dateFormatted = hasDate ? this.formatDate(item.date) : '';
            const titleElements = [
              item.title ? <Typography component="span" className="itemTitle">{item.title}</Typography> : null,
              item.publisher ? <Typography component="span" className="itemSubtitle">{item.publisher}</Typography> : null,
            ].filter(Boolean);
            const timelineCircleElement = timelineParams.isTimelineActive && hasDate ? renderTimelineElement(layoutType, timelineParams) : null;

            // Sidebar Layout Structure
            if (layoutType === 'sidebar') {
              return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ position: 'relative' }}>
                   {timelineCircleElement}
                   <Box sx={{ ml: timelineParams.isTimelineActive && !hasDate ? timelineParams.sectionContentPaddingLeft : 0 }}>
                       {hasDate && <Typography className="itemDate" sx={{ mb: 0.5 }}>{dateFormatted}</Typography>}
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'baseline', mt: hasDate ? 0 : '5px' }}>
                         {titleElements.map((el, idx) => (
                           <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                         ))}
                       </Box>
                       <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                       {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
            // Classic / Topbar Layout Structure
            else {
               return (
                 <Box key={item.id || index} className={classes.sectionItem} sx={{ display: 'flex', gap: timelineParams.isTimelineActive && hasDate ? 1 : 2, alignItems: 'flex-start' }}>
                   {(hasDate || timelineParams.isTimelineActive) ? (
                     <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0, textAlign: 'center' }}>
                        {hasDate && <Typography className="itemDate" component="div">{dateFormatted}</Typography>}
                        {!hasDate && timelineParams.isTimelineActive && <Box sx={{height: '1em'}} /> }
                     </Box>
                   ) : <Box sx={{ flexBasis: DATE_BOX_WIDTH_OTHER, flexShrink: 0 }}/>
                   }
                   {timelineCircleElement}
                   {timelineParams.isTimelineActive && !hasDate && <Box sx={{width: '10px', flexShrink: 0}} />}
                   <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', mb: 0.5, alignItems: 'baseline' }}>
                        {titleElements.map((el, idx) => (
                          <React.Fragment key={idx}>{el}{idx < titleElements.length - 1 && <span style={{ margin: '0 0.5em' }}>|</span>}</React.Fragment>
                        ))}
                      </Box>
                      <div className="richText" dangerouslySetInnerHTML={{ __html: description }} />
                      {link && <a href={link} className="itemLink" target="_blank" rel="noopener noreferrer">{link}</a>}
                   </Box>
                 </Box>
               );
            }
          })}
        </Box>
      </Box>
    );
  }

  static renderReferences(data, classes, colors, activeStyle, layoutType, selectedBackground, selectedSpecialEffectId, t) {
    if (!data?.references || data.references.length === 0) return null;
    const sectionColor = colors?.secondary || DEFAULT_QUATERNARY_COLOR;
    const sectionTitle = t ? t('inputSection.sections.References') : 'References';

    return (
      <Box className={classes.section} data-section="References">
        {renderSectionHeader(sectionTitle, faFileAlt, sectionColor, classes, activeStyle)}
        <Box className={classes.sectionContent}>
          {this.renderSubItems(data.references, (item, index) => (
            <Box key={item.id || index} className={classes.sectionItem}>
              <Typography className="itemTitle">
                {item.name}
                {item.position && ` - ${item.position}`}
              </Typography>
              <Typography className="itemSubtitle">
                {item.company}
              </Typography>
              {item.email && (
                <Typography className="itemDescription">
                  Email: {item.email}
                </Typography>
              )}
              {item.phone && (
                <Typography className="itemDescription">
                  Phone: {item.phone}
                </Typography>
              )}
              {item.reference && (
                <div className="richText" dangerouslySetInnerHTML={{ __html: this.safeRender(item.reference) }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Typography Key (unchanged)
  static getTypographyKey(typographySettings) {
    const keyParts = [
      `cvT-${typographySettings?.cvTitle?.fontSize}pt-${typographySettings?.cvTitle?.fontFamily}-${typographySettings?.cvTitle?.lineHeight}`,
      `secT-${typographySettings?.sectionTitle?.fontSize}pt-${typographySettings?.sectionTitle?.fontFamily}-${typographySettings?.sectionTitle?.lineHeight}`,
      `body-${typographySettings?.body?.fontSize}pt-${typographySettings?.body?.fontFamily}-${typographySettings?.body?.lineHeight}`,
      `small-${typographySettings?.smallText?.fontSize}pt-${typographySettings?.smallText?.fontFamily}-${typographySettings?.smallText?.lineHeight}`,
    ];
    return keyParts.join('_');
  }

  // Typography Styles (unchanged)
  static getTypographyStyles(typographySettings, design) {
    const defaultCvTitleSize = 24;
    const defaultSectionTitleSize = 12;
    const defaultBodySize = 10;
    const defaultSmallTextSize = 8.5;
    const defaultFontFamily = "'Arial', sans-serif";
    const getCategoryStyle = (categoryKey, defaultSize, defaultLineHeight) => ({
      fontSize: `${typographySettings?.[categoryKey]?.fontSize || defaultSize}pt`,
      fontFamily: typographySettings?.[categoryKey]?.fontFamily || defaultFontFamily,
      lineHeight: typographySettings?.[categoryKey]?.lineHeight || defaultLineHeight,
    });
    const styles = {
      '& .headerName': { ...getCategoryStyle('cvTitle', defaultCvTitleSize, 1.2), fontWeight: 'bold', color: design?.colors?.primary || DEFAULT_PRIMARY_COLOR },
      '& .headerTitle': { ...getCategoryStyle('body', defaultBodySize, 1.4), color: design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      '& .sectionTitle': { ...getCategoryStyle('sectionTitle', defaultSectionTitleSize, 1.3), fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '0.5em', color: design?.colors?.secondary || DEFAULT_SECONDARY_COLOR }, // Color might vary per section now
      '& .itemTitle': { ...getCategoryStyle('body', defaultBodySize, 1.4), fontWeight: 'bold', color: design?.colors?.text || DEFAULT_TEXT_COLOR },
      '& .itemSubtitle': { ...getCategoryStyle('body', defaultBodySize, 1.4), color: design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      '& .itemDescription, & .paragraph, & .richText': {
        ...getCategoryStyle('body', defaultBodySize, 1.4),
        marginTop: '0.25em',
        color: design?.colors?.text || DEFAULT_TEXT_COLOR,
        '& ul, & ol': { fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit', margin: '0.5em 0', paddingLeft: '1.5em' },
        '& li': { fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit', marginBottom: '0.25em' },
        '& p': { fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit', margin: '0.5em 0' },
        '& a': { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', color: design?.colors?.primary || DEFAULT_PRIMARY_COLOR } // General links
      },
      '& .smallText, & .contactItem, & .itemDate': { // Combined general small text, contact items, and dates
        ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3),
        color: design?.colors?.text || DEFAULT_TEXT_COLOR, // Default to main text color
      },
      '& .itemLink': { // General item links
        ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3),
        color: design?.colors?.primary || DEFAULT_PRIMARY_COLOR, 
        textDecoration: 'none' 
      },
      '& .itemDate': { color: design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      // Sidebar specific overrides (remain scoped)
      '& .sidebarSectionTitle': { ...getCategoryStyle('sectionTitle', defaultSectionTitleSize, 1.3), fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '0.5em', color: design?.colors?.sidebarText || design?.colors?.primary || DEFAULT_PRIMARY_COLOR },
      '& .sidebarItemTitle': { ...getCategoryStyle('body', defaultBodySize, 1.4), fontWeight: 'bold', color: design?.colors?.sidebarText || design?.colors?.text || DEFAULT_TEXT_COLOR },
      '& .sidebarItemSubtitle, & .sidebarRichText': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarText || design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      '& .sidebar .contactItem': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarText || design?.colors?.text || DEFAULT_TEXT_COLOR },
      '& .sidebar .itemLink': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarPrimary || design?.colors?.primary || DEFAULT_PRIMARY_COLOR, textDecoration: 'none' },
      '& .sidebar .itemDate': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarText || design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      // Topbar specific overrides (map header elements to sidebar colors)
      '& .topbar .headerName': { ...getCategoryStyle('cvTitle', defaultCvTitleSize, 1.2), fontWeight: 'bold', color: design?.colors?.sidebarText || design?.colors?.primary || DEFAULT_PRIMARY_COLOR },
      '& .topbar .headerTitle': { ...getCategoryStyle('body', defaultBodySize, 1.4), color: design?.colors?.sidebarText || design?.colors?.textSecondary || DEFAULT_TEXT_SECONDARY_COLOR },
      '& .topbar .contactItem': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarText || design?.colors?.text || DEFAULT_TEXT_COLOR },
      '& .topbar .itemLink': { ...getCategoryStyle('smallText', defaultSmallTextSize, 1.3), color: design?.colors?.sidebarPrimary || design?.colors?.primary || DEFAULT_PRIMARY_COLOR, textDecoration: 'none' },
      // Proficiency visualization classes (remain unchanged)
      '& .proficiencyVisualization': {},
      '& .proficiencyFilled': {},
      '& .proficiencyEmpty': {},
    };
    return styles;
  }
}

export default BaseLayout; 